.weather {
  position: relative;
  z-index: 1;
  /* Edge + chrome clearance is handled by the live stage safe-area
   * (--token-safe-*); the page fills its safe box. */
  padding: 0;
  font-family: var(--token-font-ui);
  color: var(--token-text-primary);
  /* flex: 1 lets us fill the .pageTransition column on the main axis
   * (parent is a flex column, align-items: stretch only spans the
   * cross axis). Without it, height: 100% won't resolve here. */
  flex: 1;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.weather > .card {
  /* max-content (not 100%) so the card keeps its natural size and never
   * reflows with the viewport; useFitScale shrinks the whole card to fit. */
  width: max-content;
}

.empty {
  position: relative;
  z-index: 1;
  padding: var(--token-space-2xl);
  font-family: var(--token-font-ui);
}

.header {
  display: flex;
  gap: var(--token-space-md);
  margin-bottom: var(--token-space-xl);
}

.navButton {
  min-height: var(--token-touch-min);
  padding: var(--token-space-sm) var(--token-space-lg);
  border-radius: var(--token-radius-pill);
  border: 1px solid rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.35);
  color: var(--token-text-primary);
  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);
  font-family: inherit;
  cursor: pointer;
}

.card {
  background: rgba(255, 250, 240, 0.7);
  backdrop-filter: blur(22px) saturate(160%);
  -webkit-backdrop-filter: blur(22px) saturate(160%);
  border: 1.5px solid var(--token-accent);
  border-radius: var(--token-radius-card);
  padding: var(--token-space-2xl);
  box-shadow:
    0 30px 60px -20px rgba(8, 18, 12, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.45);
  max-width: 1200px;
  /* No `margin: 0 auto`: on small viewports the fixed-width card is larger than
   * the page and auto margins would collapse to 0 (left-align), scaling it
   * off-centre. Parent align-items: center centers it; fit toward centre. */
  margin: 0;
  transform-origin: center center;
}

.title {
  font-family: var(--token-font-display);
  /* Fixed design px (the card scales as a unit via useFitScale). */
  font-size: 48px;
  text-align: center;
  margin: 0 0 var(--token-space-xl);
}

.kindRow {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  margin: 0 auto var(--token-space-xl);
  /* Cap the row to exactly 4 × 200px tiles + 3 × 6px gaps so the
   * 8 weather kinds wrap into a clean 4 + 4 grid centered in the card. */
  max-width: calc(4 * 200px + 3 * 6px);
}

.kindRow > .kindButton {
  flex: 0 0 200px;
  max-width: 200px;
}

.kindButton {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--token-space-xs);
  padding: var(--token-space-lg);
  min-height: var(--token-touch-min);
  border-radius: var(--token-radius-card-inner);
  border: 2px solid rgba(8, 18, 12, 0.16);
  background: rgba(255, 255, 255, 0.7);
  font-family: inherit;
  cursor: pointer;
  font-size: 16px;
}

.kindButton[aria-checked='true'] {
  background: rgba(255, 255, 255, 0.7);
  color: var(--token-text-primary);
  border: 6px solid var(--token-accent);
  box-shadow:
    0 0 12px 2px color-mix(in srgb, var(--token-accent) 70%, transparent),
    0 0 28px 8px color-mix(in srgb, var(--token-accent) 45%, transparent);
}

/* The artwork is square 256x256 source. Display at ~96px in the picker
 * — bigger than the old 36px emoji so the icons read from across the
 * room. object-fit: contain preserves the icon's natural padding. */
.kindIcon {
  width: 96px;
  height: 96px;
  object-fit: contain;
  display: block;
  pointer-events: none;
  user-select: none;
}

/* Save-failure notice under the picker. Calm but visible — replaces the
 * old silent failure where a failed save looked like a dead button. */
.saveError {
  margin: 0 auto var(--token-space-lg);
  max-width: calc(4 * 200px + 3 * 6px);
  padding: var(--token-space-sm) var(--token-space-lg);
  border-radius: var(--token-radius-card-inner);
  background: rgba(255, 235, 220, 0.92);
  border: 2px solid var(--token-accent);
  color: var(--token-text-primary);
  font-family: var(--token-font-ui);
  font-size: 15px;
  text-align: center;
}

/* Thermometer sits alone, centered, so kids' eyes go straight to the
 * slider they're meant to set. The "Right now" outdoor readout has
 * moved to the bottom-left actions row to free up this space. */
.thermometerRow {
  display: flex;
  justify-content: center;
  margin-bottom: var(--token-space-xl);
}

.thermometerWrap {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 900px;
}

/* "Right now 72° outside" — soft, secondary surface. Intentionally
 * lighter weight + opacity than the thermometer so kids' eyes go to
 * the slider they need to set. */
.localTemp {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 12px 16px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.55);
  border: 1px solid rgba(8, 18, 12, 0.1);
  font-family: var(--token-font-ui);
  color: rgba(28, 38, 32, 0.7);
  flex-shrink: 0;
}

.localTempLabel {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  opacity: 0.7;
}

.localTempValue {
  font-family: var(--token-font-display);
  font-size: 22px;
  font-weight: 700;
  color: rgba(28, 38, 32, 0.78);
  letter-spacing: 0.2px;
  line-height: 1.1;
}

.localTempHint {
  font-size: 11px;
  opacity: 0.6;
}

/* Bottom row: outdoor temp readout (left) + Weather history pill
 * (right). When there's no local temp the empty placeholder still
 * holds the left slot so the history pill stays right-aligned. */
.actionsRow {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: var(--token-space-md);
  gap: var(--token-space-md);
}

.historyButton {
  font-family: var(--token-font-ui);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.3px;
  padding: 8px 16px;
  border-radius: var(--token-radius-pill);
  border: 1px solid rgba(8, 18, 12, 0.1);
  background: rgba(255, 255, 255, 0.55);
  color: rgba(28, 38, 32, 0.7);
  cursor: pointer;
  transition:
    background 120ms ease,
    color 120ms ease;
}

.historyButton:hover {
  background: rgba(255, 255, 255, 0.75);
  color: var(--token-text-primary);
}
